/**
 * ControlPanel API (`/broadcast-points`) クライアント。
 *
 * - すべての呼び出しに `Authorization: Bearer <Cognito ID Token>` を付与する。
 * - エンドポイントは `AppConfig.apiUrl` をベースとする。
 */
import type { AppConfig } from '../config';
import type { GtfsStop, GtfsStopListFile } from '../data/gtfsStops';
import type { RouteStop } from '../data/routeStops';

import { ApiError, joinUrl } from './common';
import type {
  ApiErrorBody,
  BroadcastPoint,
  BroadcastPointCreateRequest,
  BroadcastPointListResponse,
  BroadcastPointUpdateRequest,
} from './types';

export { ApiError, joinUrl };

export interface BroadcastPointsClientOptions {
  /** Cognito ID トークンを取得する関数 (期限切れ時は AuthProvider 側で自動更新済み) */
  getIdToken: () => Promise<string>;
  /** AppConfig (apiUrl 取得用) */
  config: AppConfig;
  /** テスト時に差し込む fetch 実装 */
  fetchImpl?: typeof fetch;
}

export class BroadcastPointsClient {
  private readonly options: BroadcastPointsClientOptions;

  constructor(options: BroadcastPointsClientOptions) {
    this.options = options;
  }

  /** GET /broadcast-points (任意で routeDirection 絞り込み) */
  async list(routeDirection?: string): Promise<BroadcastPointListResponse> {
    const path = routeDirection
      ? `broadcast-points?routeDirection=${encodeURIComponent(routeDirection)}`
      : 'broadcast-points';
    return this.request<BroadcastPointListResponse>('GET', path);
  }

  /** POST /broadcast-points (新規作成) */
  async create(payload: BroadcastPointCreateRequest): Promise<BroadcastPoint> {
    return this.request<BroadcastPoint>('POST', 'broadcast-points', payload);
  }

  /** PUT /broadcast-points/{id} (更新) */
  async update(spotId: string, payload: BroadcastPointUpdateRequest): Promise<BroadcastPoint> {
    return this.request<BroadcastPoint>(
      'PUT',
      `broadcast-points/${encodeURIComponent(spotId)}`,
      payload,
    );
  }

  /** DELETE /broadcast-points/{id} */
  async remove(spotId: string, routeDirection: string): Promise<void> {
    const path = `broadcast-points/${encodeURIComponent(spotId)}?routeDirection=${encodeURIComponent(
      routeDirection,
    )}`;
    await this.request<unknown>('DELETE', path);
  }

  /**
   * POST /audio-uploads (MP3 音声ファイルをアップロードする)
   *
   * File を base64 化して送信し、保存先の `audio_key` を受け取る。
   * 受け取った `audio_key` は free_text コンテンツの `properties.audio_key` に設定する。
   */
  async uploadAudio(file: File): Promise<{ audio_key: string }> {
    const contentBase64 = await fileToBase64(file);
    return this.request<{ audio_key: string }>('POST', 'audio-uploads', {
      filename: file.name,
      content_type: 'audio/mpeg',
      content_base64: contentBase64,
    });
  }

  /**
   * GET /route-stops?route_id=...&direction_id=0
   *
   * 駅順マスタ (RouteStopList API) を ControlPanel API 経由 (Cognito 認証) で取得する。
   * 接続線遅延コンテンツの「遅延判定開始駅 / 終了駅」ドロップダウン用。
   */
  async getRouteStops(
    routeId: string,
    directionId = 0,
  ): Promise<{ route_id: string; direction_id?: number; stops: RouteStop[] }> {
    const path = `route-stops?route_id=${encodeURIComponent(
      routeId,
    )}&direction_id=${encodeURIComponent(String(directionId))}`;
    return this.request<{ route_id: string; direction_id?: number; stops: RouteStop[] }>(
      'GET',
      path,
    );
  }

  /**
   * GET /gtfs-stops[?date=YYYYMMDD]
   *
   * GTFS 静的 stops (GtfsStatic API `/gtfs/stops` のプロキシ) を ControlPanel API 経由
   * (Cognito 認証) で取得する。放送条件「終着番線」ドロップダウンの候補ソース。
   *
   * - レスポンスは `getRouteStops` の `stops` ではなく **`data`** キー
   *   (GtfsStatic のパススルー形 `{file, date, count, data}`)。本メソッドはその差異を
   *   吸収して `GtfsStop[]` に正規化して返す (欠損時は空配列)。
   * - `route_id` では絞り込めない (`stops.txt` に `route_id` カラムが無い) ため
   *   **全件取得**になる。呼び出し側でキャッシュし、駅選択後に `parent_station` で
   *   フィルタすること (`data/gtfsStops.ts` の `findPlatformStopIds` を使う)。
   * - `date` 省略時は GTFS 配置済みの最新日付が採用される (日付跨ぎ直後は翌日ダイヤの
   *   stops が返り得る)。運行日を固定したい場合のみ `YYYYMMDD` を指定する。
   */
  async getGtfsStops(date?: string): Promise<GtfsStop[]> {
    const path = date ? `gtfs-stops?date=${encodeURIComponent(date)}` : 'gtfs-stops';
    const res = await this.request<GtfsStopListFile>('GET', path);
    return res.data ?? [];
  }

  /**
   * POST /audio-preview (SSML / 任意テキストの音声プレビュー)
   *
   * 任意テキストまたは SSML を送り、Polly で合成した MP3 を base64 で受け取る。
   * 受け取った base64 は {@link base64ToAudioUrl} で再生用の object URL に変換できる。
   *
   * @param payload `text`（任意テキスト）または `ssml`（SSML 文字列）のいずれかを指定する。
   *   `text` を SSML として扱う場合は `isSsml: true` を指定する。
   *   `voiceId` に Polly VoiceId (Takumi / Kazuha / Tomoko) を指定すると、
   *   実放送と同じ声色で試聴できる (未指定時はサーバー側デフォルトの Takumi)。
   */
  async previewAudio(payload: {
    text?: string;
    ssml?: string;
    isSsml?: boolean;
    voiceId?: string;
  }): Promise<{ content_type: string; content_base64: string }> {
    // voice_id はサーバー側でも「未指定 = Takumi」にフォールバックするが、
    // 空文字を送るとバリデーションで弾かれるため、空文字 / undefined はキーごと省く。
    const body: Record<string, unknown> = {
      text: payload.text,
      ssml: payload.ssml,
      is_ssml: payload.isSsml ?? false,
    };
    if (typeof payload.voiceId === 'string' && payload.voiceId !== '') {
      body.voice_id = payload.voiceId;
    }
    return this.request<{ content_type: string; content_base64: string }>(
      'POST',
      'audio-preview',
      body,
    );
  }

  // ---------------------------------------------------------------------
  // 内部実装
  // ---------------------------------------------------------------------

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = joinUrl(this.options.config.apiUrl, path);
    const idToken = await this.options.getIdToken();
    const fetchImpl = this.options.fetchImpl ?? fetch;

    const res = await fetchImpl(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!res.ok) {
      let errorBody: ApiErrorBody | undefined;
      try {
        errorBody = (await res.json()) as ApiErrorBody;
      } catch {
        // body が JSON で無い場合は無視
      }
      const message = errorBody?.message ?? `API request failed: HTTP ${res.status}`;
      throw new ApiError(res.status, message, errorBody);
    }

    // 204 No Content の場合は空 object を返す
    if (res.status === 204) {
      return undefined as T;
    }

    return (await res.json()) as T;
  }
}

/**
 * File を base64 文字列 (data URL プレフィックスなし) へ変換する。
 * FileReader.readAsDataURL の結果から `data:...;base64,` プレフィックスを除去して返す。
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read file as base64'));
        return;
      }
      const commaIndex = result.indexOf(',');
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
    reader.readAsDataURL(file);
  });
}

/**
 * base64 でエンコードされた音声データを、再生可能な object URL (blob:) に変換する。
 *
 * `previewAudio` が返した `content_base64` を `<audio>` 要素などで再生する用途。
 * 使い終わった URL は呼び出し側で `URL.revokeObjectURL` を実行して解放すること。
 */
export function base64ToAudioUrl(
  contentBase64: string,
  contentType: string = 'audio/mpeg',
): string {
  const binary = atob(contentBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: contentType });
  return URL.createObjectURL(blob);
}
